import { decodeMcpServerSetupDetail } from '@reflex/shared';
import type { AxonEvent } from '@/lib/types';
import { parseEventPayload } from '@reflex/ui/client/event-utils';
import { resolveIcon } from './icons';
import type { RichStep, SetupStepInfo } from './types';

function parseInitPayload(detail: string): RichStep[] | null {
  try {
    const parsed: unknown = JSON.parse(detail);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    const first = parsed[0];
    if (typeof first === 'object' && first !== null && 'id' in first) {
      return parsed as RichStep[];
    }
    // Legacy: plain string array — no label/icon metadata
    if (typeof first === 'string') {
      return (parsed as string[]).map((id) => ({ id, label: id }));
    }
  } catch {
    /* ignore malformed init detail */
  }
  return null;
}

export interface ParsedSetupEvents {
  expectedSteps: RichStep[];
  /** Steps whose latest event terminally resolved them (done/failed). */
  completedSteps: string[];
  /** Steps reported but still running (latest event was `terminal: false`). */
  inProgressSteps: string[];
  detailMap: Map<string, string>;
  durationMap: Map<string, number>;
  initializingMsMap: Map<string, number>;
}

export function parseSetupEvents(setupEvents: AxonEvent[]): ParsedSetupEvents {
  let expected: RichStep[] = [];
  // Insertion-ordered list of every reported step, plus a set of the ones
  // whose most recent event was non-terminal. A step counts as done unless
  // it is currently in `nonTerminal`; a later terminal event clears it.
  const reported: string[] = [];
  const nonTerminal = new Set<string>();
  const details = new Map<string, string>();
  const durations = new Map<string, number>();
  const initializingMsByStep = new Map<string, number>();

  for (const event of setupEvents) {
    const payload = parseEventPayload<{
      step?: string;
      detail?: string;
      durationMs?: number;
      initializingMs?: number;
      terminal?: boolean;
    }>(event.payload);
    if (!payload?.step) continue;

    if (payload.step === 'init' && payload.detail) {
      const parsed = parseInitPayload(payload.detail);
      if (parsed) expected = parsed;
      continue;
    }

    if (payload.step === 'init.append' && payload.detail) {
      const parsed = parseInitPayload(payload.detail);
      if (parsed) {
        const existing = new Set(expected.map((s) => s.id));
        expected = [...expected, ...parsed.filter((s) => !existing.has(s.id))];
      }
      continue;
    }

    if (payload.step !== 'init' && payload.step !== 'init.append') {
      if (!reported.includes(payload.step)) reported.push(payload.step);
      // `terminal` absent means terminal (steps emitted once at completion).
      // Only an explicit `terminal: false` keeps a step in progress.
      if (payload.terminal === false) nonTerminal.add(payload.step);
      else nonTerminal.delete(payload.step);
    }
    if (payload.detail) {
      details.set(payload.step, payload.detail);
    }
    if (typeof payload.durationMs === 'number') {
      durations.set(payload.step, payload.durationMs);
    }
    if (typeof payload.initializingMs === 'number') {
      initializingMsByStep.set(payload.step, payload.initializingMs);
    }
  }

  return {
    expectedSteps: expected,
    completedSteps: reported.filter((id) => !nonTerminal.has(id)),
    inProgressSteps: reported.filter((id) => nonTerminal.has(id)),
    detailMap: details,
    durationMap: durations,
    initializingMsMap: initializingMsByStep,
  };
}

export function buildStepsFromEvents({
  expectedSteps,
  completedSteps,
  inProgressSteps,
  detailMap,
  durationMap,
  initializingMsMap,
}: ParsedSetupEvents): SetupStepInfo[] {
  const richSteps: RichStep[] =
    expectedSteps.length > 0
      ? expectedSteps
      : [...completedSteps, ...inProgressSteps].map((id) => ({ id, label: id }));

  return richSteps.map((s) => {
    const detail = detailMap.get(s.id);
    const mcpDetail = decodeMcpServerSetupDetail(detail) ?? undefined;
    return {
      id: s.id,
      label: s.label,
      icon: resolveIcon(s.icon),
      detail: mcpDetail ? mcpDetail.summary : detail,
      mcpDetail,
      ...(durationMap.get(s.id) != null ? { durationMs: durationMap.get(s.id) } : {}),
      ...(initializingMsMap.get(s.id) != null
        ? { initializingMs: initializingMsMap.get(s.id) }
        : {}),
      ...(s.blocking ? { blocking: true } : {}),
      // Legacy init payloads omit `group`; keep the devbox row as a top-level
      // provisioning line by id so it doesn't fall into the initializing
      // section. Everything else defaults to initializing in the renderer.
      ...(s.group
        ? { group: s.group }
        : s.id === 'creating_devbox'
          ? { group: 'provisioning' as const }
          : {}),
      ...(detail?.startsWith('failed:') ? { failed: true } : {}),
    };
  });
}

/**
 * Extract the log section for a single server from the parent step's combined
 * log file. The on-disk log is grouped by `### <name> ###` banners written by
 * the setup hook's `wrapWithStatus`, so we slice between the matching banner
 * and the next one. Returns `null` if no section is found, so callers can
 * render a friendly empty state instead of the full log.
 */
export function extractServerLogSection(log: string, serverName: string): string | null {
  const banner = `### ${serverName} ###`;
  const startIdx = log.indexOf(banner);
  if (startIdx === -1) return null;
  const contentStart = startIdx + banner.length;
  const remainder = log.slice(contentStart);
  const nextMatch = remainder.match(/\n###\s.+?\s###/);
  const endIdx = nextMatch?.index != null ? contentStart + nextMatch.index : log.length;
  return log.slice(contentStart, endIdx).trim();
}
